import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { Plus, Copy, ToggleLeft, ToggleRight, Trash2, ExternalLink, Loader2, KeyRound, Percent, Pencil, AlertTriangle, Users, Wallet, TrendingUp, Coins, Clock } from "lucide-react";
import { toast } from "sonner";

export default function IndicatorsPage() {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState({}); // { [indicator_id]: {clientes_ativos, capital_ativo, juros_recebidos_mes, comissao_mes, ultimo_contrato_data} }
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", active: true, commission_percent: 5 });
  const [saving, setSaving] = useState(false);
  const [loginModal, setLoginModal] = useState(null); // indicator
  const [loginPassword, setLoginPassword] = useState("");
  const [loginSaving, setLoginSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const baseUrl = window.location.origin;

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, metricsRes] = await Promise.all([
        api.get("/indicators"),
        api.get("/indicators/metrics").catch(() => ({ data: [] })),
      ]);
      setItems(listRes.data);
      const m = {};
      (metricsRes.data || []).forEach(x => { m[x.id] = x; });
      setMetrics(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/indicators", { ...form, commission_percent: Number(form.commission_percent) || 5 });
      toast.success("Indicador criado");
      setOpen(false);
      setForm({ name: "", whatsapp: "", email: "", active: true, commission_percent: 5 });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar");
    } finally { setSaving(false); }
  };

  const toggle = async (i) => {
    try {
      await api.patch(`/indicators/${i.id}`, { active: !i.active });
      load();
    } catch (_) { toast.error("Erro ao atualizar"); }
  };

  const remove = async (i) => {
    if (!window.confirm(`Excluir indicador "${i.name}"?`)) return;
    try {
      await api.delete(`/indicators/${i.id}`);
      toast.success("Indicador excluído");
      load();
    } catch (_) { toast.error("Erro ao excluir"); }
  };

  const createLogin = async (e) => {
    e.preventDefault();
    if (!loginModal) return;
    setLoginSaving(true);
    try {
      const { data } = await api.post(`/indicators/${loginModal.id}/login`, { password: loginPassword });
      toast.success(data.updated ? "Senha atualizada" : "Login criado");
      setLoginModal(null);
      setLoginPassword("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar login");
    } finally { setLoginSaving(false); }
  };

  const linkFor = (code) => `${baseUrl}/cadastro?indicador=${encodeURIComponent(code)}`;

  const openEdit = (i) => {
    setEditing(i);
    setEditForm({
      name: i.name || "",
      email: i.email || "",
      whatsapp: i.whatsapp || "",
      code: i.code || "",
      commission_percent: i.commission_percent ?? 5,
      active: i.active !== false,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing || !editForm) return;
    if (!editForm.name.trim()) { toast.error("Nome obrigatório"); return; }
    if (!editForm.whatsapp.trim() || editForm.whatsapp.replace(/\D/g, "").length < 10) { toast.error("WhatsApp inválido (mín. 10 dígitos)"); return; }
    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) { toast.error("E-mail inválido"); return; }
    if (!editForm.code.trim() || !/^[A-Za-z0-9_-]+$/.test(editForm.code.trim())) {
      toast.error("Código deve conter apenas letras, números, traço ou underline");
      return;
    }
    const pct = Number(editForm.commission_percent);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Comissão deve estar entre 0 e 100"); return; }

    const oldPct = Number(editing.commission_percent ?? 5);
    if (pct !== oldPct) {
      const ok = window.confirm(
        `Alterar comissão de ${oldPct}% para ${pct}%?\n\n` +
        `Os contratos/clientes JÁ existentes mantêm a comissão antiga (${oldPct}%). ` +
        `Apenas novos cadastros futuros usarão ${pct}%.`
      );
      if (!ok) return;
    }

    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase() || null,
        whatsapp: editForm.whatsapp.trim(),
        code: editForm.code.trim(),
        commission_percent: pct,
        active: editForm.active,
      };
      await api.patch(`/indicators/${editing.id}`, payload);
      toast.success("Indicador atualizado");
      setEditing(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setEditSaving(false); }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-7 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Gestão</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Indicadores</h1>
          <p className="text-[#A3A3A3] text-[13px] sm:text-sm mt-1">Cadastre e gerencie os parceiros indicadores e seus links.</p>
        </div>
        <button onClick={()=>setOpen(true)} className="fc-btn-gold flex items-center gap-2 flex-shrink-0" data-testid="btn-new-indicator">
          <Plus size={15}/> Novo indicador
        </button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && [1,2,3].map(i => <div key={i} className="fc-card h-44 shimmer"/>)}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center text-[#737373] py-16 fc-card">
            Nenhum indicador cadastrado.
          </div>
        )}
        {!loading && items.map(i => (
          <div key={i.id} className="fc-card p-5 fc-card-hover overflow-hidden min-w-0" data-testid={`indicator-card-${i.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-display text-[17px] font-semibold tracking-tight truncate">{i.name}</div>
                <div className="text-[12px] text-[#A3A3A3] mt-1 break-all">{i.whatsapp} {i.email && `· ${i.email}`}</div>
              </div>
              <button onClick={()=>toggle(i)} className="text-[#A3A3A3] hover:text-white flex-shrink-0" title={i.active ? "Desativar" : "Ativar"}>
                {i.active ? <ToggleRight size={26} className="text-[#D4AF37]"/> : <ToggleLeft size={26}/>}
              </button>
            </div>

            <div className="mt-5 p-3 rounded-md bg-[#0f0f0f] border border-[#262626] min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#737373]">Código</div>
                  <div className="font-mono-tab text-[#D4AF37] text-sm mt-0.5 break-all">{i.code}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#737373]">Comissão</div>
                  <div className="font-mono-tab text-sm mt-0.5 text-white">{i.commission_percent ?? 5}%</div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#737373] mt-3">Link público</div>
              <div className="flex items-start gap-2 mt-1">
                <code className="text-[11px] text-[#A3A3A3] break-all flex-1 min-w-0 leading-snug" title={linkFor(i.code)}>{linkFor(i.code)}</code>
                <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                  <button onClick={()=>{navigator.clipboard.writeText(linkFor(i.code)); toast.success("Link copiado")}} className="text-[#A3A3A3] hover:text-[#D4AF37]" title="Copiar" data-testid={`copy-link-${i.id}`}>
                    <Copy size={14}/>
                  </button>
                  <a href={linkFor(i.code)} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#D4AF37]" title="Abrir">
                    <ExternalLink size={14}/>
                  </a>
                </div>
              </div>
            </div>

            {/* Faixa de métricas do indicador */}
            <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`indicator-metrics-${i.id}`}>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-2.5 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-[#737373] flex items-center gap-1">
                  <Users size={10} className="text-[#D4AF37]"/> Clientes ativos
                </div>
                <div className="font-mono-tab text-[14px] text-[#E5E5E5] mt-0.5">
                  {metrics[i.id]?.clientes_ativos ?? "—"}
                </div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-2.5 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-[#737373] flex items-center gap-1">
                  <Wallet size={10} className="text-[#D4AF37]"/> Capital ativo
                </div>
                <div className="font-mono-tab text-[13px] text-[#D4AF37] mt-0.5">
                  {metrics[i.id] ? formatBRL(metrics[i.id].capital_ativo) : "—"}
                </div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-2.5 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-[#737373] flex items-center gap-1">
                  <TrendingUp size={10} className="text-emerald-300"/> Juros recebidos (mês)
                </div>
                <div className="font-mono-tab text-[13px] text-emerald-300 mt-0.5">
                  {metrics[i.id] ? formatBRL(metrics[i.id].juros_recebidos_mes) : "—"}
                </div>
              </div>
              <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-2.5 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-[#737373] flex items-center gap-1">
                  <Coins size={10} className="text-amber-300"/> Comissão do mês
                </div>
                <div className="font-mono-tab text-[13px] text-amber-300 mt-0.5">
                  {metrics[i.id] ? formatBRL(metrics[i.id].comissao_mes) : "—"}
                </div>
              </div>
              <div className="col-span-2 rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-2.5 py-2">
                <div className="text-[9.5px] uppercase tracking-wider text-[#737373] flex items-center gap-1">
                  <Clock size={10}/> Último contrato
                </div>
                <div className="font-mono-tab text-[12px] text-[#A3A3A3] mt-0.5">
                  {metrics[i.id]?.ultimo_contrato_data ? formatDateBR(metrics[i.id].ultimo_contrato_data) : "—"}
                </div>
              </div>
              {/* iter68 — Recuperação */}
              {metrics[i.id] && (metrics[i.id].qtd_recuperacao > 0 || metrics[i.id].capital_recuperacao > 0 || metrics[i.id].recuperacao_encerrada_total > 0) && (
                <div className="col-span-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-2" data-testid={`ind-recovery-${i.id}`}>
                  <div className="text-[9.5px] uppercase tracking-wider text-amber-300 flex items-center gap-1 mb-1">
                    <AlertTriangle size={10}/> Recuperação
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div>
                      <div className="text-[9px] text-[#737373]">Capital</div>
                      <div className="font-mono-tab text-amber-300">{formatBRL(metrics[i.id].capital_recuperacao)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-[#737373]">Qtd</div>
                      <div className="font-mono-tab text-amber-300">{metrics[i.id].qtd_recuperacao || 0}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-[#737373]">Taxa recup.</div>
                      <div className="font-mono-tab text-emerald-300">{(metrics[i.id].taxa_recuperacao || 0).toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-5 gap-2">
              <span className={`badge-base ${i.active ? "badge-aprovado" : "bg-neutral-800 text-neutral-400"}`}>{i.active ? "Ativo" : "Inativo"}</span>
              <div className="flex items-center gap-3">
                <button onClick={()=>setLoginModal(i)} className="text-[#D4AF37]/80 hover:text-[#D4AF37] text-xs flex items-center gap-1" data-testid={`create-login-${i.id}`}>
                  <KeyRound size={13}/> Login
                </button>
                <button onClick={()=>openEdit(i)} className="text-[#A3A3A3] hover:text-white text-xs flex items-center gap-1" data-testid={`edit-indicator-${i.id}`}>
                  <Pencil size={13}/> Editar
                </button>
                <button onClick={()=>remove(i)} className="text-red-400/70 hover:text-red-300 text-xs flex items-center gap-1" data-testid={`delete-indicator-${i.id}`}>
                  <Trash2 size={13}/> Excluir
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setOpen(false)}>
          <form onSubmit={create} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="indicator-form-modal">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Novo</div>
            <h3 className="font-display text-2xl font-bold mt-1">Indicador</h3>
            <div className="space-y-4 mt-5">
              <div>
                <label className="fc-label">Nome</label>
                <input required className="fc-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="indicator-name"/>
              </div>
              <div>
                <label className="fc-label">WhatsApp</label>
                <input required className="fc-input" value={form.whatsapp} onChange={e=>setForm({...form, whatsapp: e.target.value})} data-testid="indicator-whatsapp"/>
              </div>
              <div>
                <label className="fc-label">E-mail (opcional)</label>
                <input type="email" className="fc-input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="indicator-email"/>
              </div>
              <div>
                <label className="fc-label">Comissão (%)</label>
                <div className="relative">
                  <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373]"/>
                  <input type="number" step="0.5" min="0" max="100" className="fc-input" value={form.commission_percent} onChange={e=>setForm({...form, commission_percent: e.target.value})} data-testid="indicator-commission"/>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                <input type="checkbox" checked={form.active} onChange={e=>setForm({...form, active: e.target.checked})}/> Ativo
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setOpen(false)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="indicator-save">
                {saving && <Loader2 size={15} className="animate-spin"/>} Criar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Create login modal */}
      {loginModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setLoginModal(null)}>
          <form onSubmit={createLogin} className="fc-card w-full max-w-md p-6" onClick={e=>e.stopPropagation()} data-testid="indicator-login-modal">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Acesso ao painel</div>
            <h3 className="font-display text-2xl font-bold mt-1">Login para {loginModal.name}</h3>
            <p className="text-[12.5px] text-[#A3A3A3] mt-2">
              {loginModal.email
                ? <>Vamos criar/atualizar um usuário com e-mail <span className="text-white">{loginModal.email}</span> vinculado a este indicador.</>
                : <span className="text-red-400">Este indicador não tem e-mail cadastrado. Edite-o e adicione um e-mail antes.</span>}
            </p>
            <div className="mt-5">
              <label className="fc-label">Senha (mín. 6 caracteres)</label>
              <input
                type="password" minLength={6} required
                className="fc-input"
                value={loginPassword}
                onChange={e=>setLoginPassword(e.target.value)}
                disabled={!loginModal.email}
                data-testid="indicator-login-password"
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setLoginModal(null)} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={!loginModal.email || loginSaving} className="fc-btn-gold flex items-center gap-2 disabled:opacity-50" data-testid="indicator-login-save">
                {loginSaving && <Loader2 size={15} className="animate-spin"/>} Salvar
              </button>
            </div>
          </form>
        </div>
      )}
      {editing && editForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>{setEditing(null); setEditForm(null);}}>
          <form onSubmit={saveEdit} className="fc-card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="indicator-edit-modal">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Editar</div>
            <h3 className="font-display text-2xl font-bold mt-1">{editing.name}</h3>
            <div className="space-y-4 mt-5">
              <div>
                <label className="fc-label">Nome</label>
                <input required className="fc-input" value={editForm.name} onChange={e=>setEditForm({...editForm, name: e.target.value})} data-testid="indicator-edit-name"/>
              </div>
              <div>
                <label className="fc-label">WhatsApp</label>
                <input required className="fc-input" placeholder="(11) 99999-9999" value={editForm.whatsapp} onChange={e=>setEditForm({...editForm, whatsapp: e.target.value})} data-testid="indicator-edit-whatsapp"/>
              </div>
              <div>
                <label className="fc-label">E-mail (opcional, necessário para login)</label>
                <input type="email" className="fc-input" value={editForm.email} onChange={e=>setEditForm({...editForm, email: e.target.value})} data-testid="indicator-edit-email"/>
              </div>
              <div>
                <label className="fc-label">Código / Link público</label>
                <input required className="fc-input font-mono" value={editForm.code} onChange={e=>setEditForm({...editForm, code: e.target.value.toUpperCase()})} data-testid="indicator-edit-code"/>
                <p className="text-[11px] text-[#737373] mt-1">Apenas letras, números, traço ou underline. Será o final do link de cadastro.</p>
              </div>
              <div>
                <label className="fc-label">Comissão (%)</label>
                <div className="relative">
                  <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373]"/>
                  <input type="number" step="0.5" min="0" max="100" className="fc-input" value={editForm.commission_percent} onChange={e=>setEditForm({...editForm, commission_percent: e.target.value})} data-testid="indicator-edit-commission"/>
                </div>
                {Number(editForm.commission_percent) !== Number(editing.commission_percent ?? 5) && (
                  <div className="mt-2 flex items-start gap-2 text-[11.5px] text-amber-300 bg-amber-500/5 border border-amber-500/20 rounded-md p-2.5" data-testid="commission-warning">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5"/>
                    <span>Comissões já lançadas para clientes existentes serão preservadas em <strong>{editing.commission_percent ?? 5}%</strong>. Apenas novos cadastros futuros usarão <strong>{editForm.commission_percent}%</strong>.</span>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                <input type="checkbox" checked={editForm.active} onChange={e=>setEditForm({...editForm, active: e.target.checked})} data-testid="indicator-edit-active"/> Ativo
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>{setEditing(null); setEditForm(null);}} className="fc-btn-ghost">Cancelar</button>
              <button type="submit" disabled={editSaving} className="fc-btn-gold flex items-center gap-2" data-testid="indicator-edit-save">
                {editSaving && <Loader2 size={15} className="animate-spin"/>} Salvar
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
